import { useEffect, useState, useCallback } from "react";

import { useMsal } from "@azure/msal-react";

import { ClipboardList } from "lucide-react";

import { callApi } from "../api/api";

import ResponsiveTableCards from "../components/ResponsiveTableCards";
import ScrollableTable from "../components/ScrollableTable";
import Pagination from "../components/Pagination";
import { useBreakpoint } from "../hooks/useBreakpoint";

export default function UserRoleAudit() {
  const { instance, accounts } = useMsal();

  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);

  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");

  const pageSize = 25;

  const isCardView = useBreakpoint(1024);


  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const res = await callApi(
        instance,
        accounts,
        "GET",
        "/api/admin/user-role-audit",
        null,
        {
          params: {
            page,
            pageSize,
            search,
          },
        },
      );

      setRows(res?.data?.data || []);

      setTotal(res?.data?.total || 0);
    } catch (err) {
      console.error(err);

      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, page, search]);

  useEffect(() => {
    if (accounts.length) {
      loadData();
    }
  }, [accounts.length, loadData]);

  const totalPages = Math.ceil(total / pageSize);

  /* ================= RESPONSIVE CARD MAPPERS ================= */

  const mapAuditCard = useCallback(
    (r) => ({
      id: `audit-${r.user_role_id}`,
      icon: <ClipboardList size={16} />,
      tone: "success",
      title: r.user_name || r.email_address || "—",
      subtitle: r.email_address || "—",
      meta: [
        { label: "Role", value: r.role_name || "—" },
        { label: "Code", value: r.role_code || "—" },
        {
          label: "Effective From",
          value: r.effective_from
            ? new Date(r.effective_from).toLocaleDateString()
            : "—",
        },
        {
          label: "Effective To",
          value: r.effective_to
            ? new Date(r.effective_to).toLocaleDateString()
            : "Active",
        },
      ],
      badges: [{ value: "Active", tone: "success" }],
    }),
    [],
  );

  const mapAuditDrawer = useCallback(
    (r) => ({
      title: r.user_name || r.email_address || "—",
      fields: [
        { label: "Email", value: r.email_address || "—" },
        { label: "Role", value: r.role_name || "—" },
        { label: "Role Code", value: r.role_code || "—" },
        {
          label: "Effective From",
          value: r.effective_from
            ? new Date(r.effective_from).toLocaleDateString()
            : "—",
        },
        {
          label: "Effective To",
          value: r.effective_to
            ? new Date(r.effective_to).toLocaleDateString()
            : "Active",
        },
        { label: "Status", value: "Active" },
      ],
    }),
    [],
  );

  const renderTable = () => (
    <>
      <ScrollableTable style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>User</th>

              <th style={styles.th}>Email</th>

              <th style={styles.th}>Role</th>

              <th style={styles.th}>Code</th>

              <th style={styles.th}>Effective From</th>

              <th style={styles.th}>Effective To</th>

              <th style={styles.th}>Status</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" style={styles.empty}>
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan="7" style={styles.empty}>
                  No records found
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.user_role_id}>
                  <td style={styles.td}>{r.user_name}</td>

                  <td style={styles.td}>{r.email_address}</td>

                  <td style={styles.td}>{r.role_name}</td>

                  <td style={styles.td}>{r.role_code}</td>

                  <td style={styles.td}>
                    {r.effective_from
                      ? new Date(r.effective_from).toLocaleDateString()
                      : "—"}
                  </td>

                  <td style={styles.td}>
                    {r.effective_to
                      ? new Date(r.effective_to).toLocaleDateString()
                      : "Active"}
                  </td>

                  <td style={styles.td}>
                    <span style={styles.active}>Active</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </ScrollableTable>
    </>
  );

  const paginationProps =
    total > 0
      ? {
          page,
          totalPages: totalPages || 1,
          totalRecords: total,
          pageSize,
          currentPageCount: rows.length,
          onPageChange: setPage,
        }
      : null;

  return (
    <div className="page-container" style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>User Role Audit</h2>

          <p style={styles.sub}>
            Enterprise audit view of all role assignments.
          </p>
        </div>

        <div style={styles.stats}>{total} Assignments</div>
      </div>

      <div style={styles.card}>
        <div style={styles.toolbar}>
          <input
            style={styles.search}
            placeholder="Search users or roles..."
            value={search}
            onChange={(e) => {
              setPage(1);

              setSearch(e.target.value);
            }}
          />
        </div>

        <ResponsiveTableCards
          breakpoint={1024}
          data={rows}
          loading={loading}
          emptyTitle="No Audit Records"
          emptySub="No role assignments found for the current search."
          listTitle="User Role Assignments"
          listSubtitle="Audit view of all role memberships"
          cardMapper={mapAuditCard}
          drawerMapper={mapAuditDrawer}
          pagination={paginationProps || undefined}
          renderTable={renderTable}
        />
      </div>

      {!isCardView && paginationProps && (
        <Pagination {...paginationProps} />
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: 24,
    background: "#f8fafc",
    minHeight: "80vh",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 20,
  },

  title: {
    margin: 0,
  },

  sub: {
    color: "#64748b",
  },

  stats: {
    background: "#fff",
    padding: "14px 18px",
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    fontWeight: 600,
  },

  card: {
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    overflow: "hidden",
  },

  toolbar: {
    padding: 20,
    borderBottom: "1px solid #e2e8f0",
  },

  search: {
    width: 320,
    padding: 12,
    borderRadius: 10,
    border: "1px solid #cbd5e1",
  },

  tableWrap: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
  },

  th: {
    textAlign: "left",
    background: "#f8fafc",
    padding: 14,
    fontSize: 12,
    color: "#64748b",
  },

  td: {
    padding: 16,
    borderTop: "1px solid #f1f5f9",
  },

  empty: {
    padding: 40,
    textAlign: "center",
  },

  active: {
    background: "#dcfce7",
    color: "#15803d",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
  },

  pagination: {
    marginTop: 20,
    display: "flex",
    justifyContent: "space-between",
  },
};
